import { buildTracePropagationTargets } from "@fluentry/domain";
import { detectNativeMobilePlatform } from "@fluentry/platform-adapters";
import { resolveProductAppApiBaseUrl } from "./auth-client";
import { clientEnv } from "./client-env";
import { getBooleanFlag, getBooleanFlagSync } from "./feature-flags";

const SENTRY_ENABLE_FLAG = "observability.sentry.product-app";

let sentryInitialized = false;
let sentryClientPromise: Promise<{
  capacitor: typeof import("@sentry/capacitor");
  react: typeof import("@sentry/react");
}> | null = null;

function loadSentryClient() {
  // Load the observability SDK only when DSN-gated code actually needs it; keeping Sentry out of the initial app chunk makes the workbench usable faster.
  sentryClientPromise ??= Promise.all([import("@sentry/capacitor"), import("@sentry/react")]).then(
    ([capacitor, react]) => ({ capacitor, react }),
  );
  return sentryClientPromise;
}

function parseSampleRate(raw: string | undefined, fallback: number) {
  const parsed = Number.parseFloat(raw ?? "");
  return Number.isFinite(parsed) ? parsed : fallback;
}

function resolveRelease() {
  return clientEnv.VITE_SENTRY_RELEASE;
}

function resolveRuntimeTags() {
  const nativePlatform = detectNativeMobilePlatform();

  // Stamp a stable runtime taxonomy up front so one Sentry project can still be sliced cleanly into web, iOS, and Android traffic.
  if (nativePlatform) {
    return {
      runtime_channel: nativePlatform,
      runtime_platform: nativePlatform,
      surface_kind: "native",
    } as const;
  }

  return {
    runtime_channel: "web",
    runtime_platform: "browser",
    surface_kind: "browser",
  } as const;
}

export function initSentry() {
  if (sentryInitialized) {
    return;
  }

  const dsn = clientEnv.VITE_SENTRY_DSN;
  if (typeof dsn !== "string" || dsn.length === 0) {
    return;
  }

  if (!getBooleanFlagSync(SENTRY_ENABLE_FLAG, true)) {
    return;
  }

  const runtimeTags = resolveRuntimeTags();

  void loadSentryClient().then(({ capacitor, react }) => {
    if (sentryInitialized) {
      return;
    }

    capacitor.init(
      {
        dsn,
        enableAutoSessionTracking: true,
        enableCaptureFailedRequests: true,
        enableNative: true,
        enableNativeCrashHandling: true,
        environment: clientEnv.VITE_APP_ENV ?? (clientEnv.DEV ? "dev" : "prod"),
        release: resolveRelease(),
        // Initialize the Capacitor wrapper with the same browser integrations so native crashes and web traces land in one release envelope.
        integrations: [capacitor.browserTracingIntegration(), capacitor.replayIntegration()],
        tracePropagationTargets: buildTracePropagationTargets(resolveProductAppApiBaseUrl()),
        tracesSampleRate: parseSampleRate(clientEnv.VITE_SENTRY_TRACES_SAMPLE_RATE, 0.1),
        replaysSessionSampleRate: parseSampleRate(
          clientEnv.VITE_SENTRY_REPLAY_SESSION_SAMPLE_RATE,
          0.02,
        ),
        replaysOnErrorSampleRate: parseSampleRate(
          clientEnv.VITE_SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE,
          1,
        ),
        initialScope: {
          tags: {
            surface: "product-app",
            ...runtimeTags,
          },
        },
      },
      react.init,
    );

    sentryInitialized = true;
  });
}

export function setProductAppUserContext(context: {
  user?: {
    id?: string | null;
    email?: string | null;
    username?: string | null;
    role?: string | null;
  } | null;
  activeOrganizationId?: string | null;
  isNativeSurface: boolean;
}) {
  if (!sentryInitialized) {
    return;
  }

  const runtimeTags = resolveRuntimeTags();

  void loadSentryClient().then(({ capacitor }) => {
    // Keep auth and workspace identity on the shared scope so UI, network, and replay evidence all point at the same tenant context.
    capacitor.setUser(
      context.user?.id
        ? {
            id: context.user.id,
            email: context.user.email ?? undefined,
            username: context.user.username ?? undefined,
          }
        : null,
    );
    capacitor.setTags({
      active_organization_id: context.activeOrganizationId ?? "personal",
      is_native_surface: String(context.isNativeSurface),
      session_role: context.user?.role ?? "guest",
      ...runtimeTags,
    });
  });
}

export async function captureProductAppException(
  error: unknown,
  context?: {
    action?: string;
    extras?: Record<string, unknown>;
    feature?: string;
    tags?: Record<string, string | number | boolean | null | undefined>;
  },
) {
  const dsn = clientEnv.VITE_SENTRY_DSN;
  if (typeof dsn !== "string" || dsn.length === 0) {
    return;
  }

  const enabled = await getBooleanFlag(SENTRY_ENABLE_FLAG, true);
  if (!enabled) {
    return;
  }

  const { capacitor } = await loadSentryClient();
  capacitor.withScope((scope) => {
    scope.setTag("feature", context?.feature ?? "product-app");
    if (context?.action) {
      scope.setTag("action", context.action);
    }
    for (const [key, value] of Object.entries(context?.tags ?? {})) {
      if (value !== undefined && value !== null) {
        scope.setTag(key, String(value));
      }
    }
    for (const [key, value] of Object.entries(context?.extras ?? {})) {
      scope.setExtra(key, value);
    }
    capacitor.captureException(error instanceof Error ? error : new Error(String(error)));
  });
}
